import { apiClient, isRemoteApiEnabled } from '@/api/client'
import categoriesData from '@/data/categories.json'

/** @typedef {import('@/types').Category} Category */

/**
 * Icon by keyword in the slug/name, so a category the owner adds in the admin
 * ("Baby Bottles & Nipples", "Bath & Skincare") gets a sensible icon without a
 * code change. First match wins; order the specific ones first.
 */
const ICON_RULES = [
  [/bottle|nipple|feed|milk/, 'milk'],
  [/pump|breast/, 'heart-pulse'],
  [/cloth|dress|wear|onesie|romper/, 'shirt'],
  [/nurs|mother|mom|maternity/, 'baby'],
  [/toy|play|rattle/, 'star'],
  [/bath|wash|skin|soap|towel/, 'bath'],
  [/sleep|bed|blanket|swaddle/, 'moon'],
  [/diaper|nappy|wipe/, 'package'],
]

/** Soft pastels, cycled by position so neighbouring cards never share one. */
const CARD_COLORS = ['#FFE0DE', '#FFF3E0', '#E8F5E9', '#E3F2FD', '#FCE4EC', '#FFF8E1', '#EDE7F6', '#E0F7FA']

/** @param {any} category @param {number} [index] @returns {Category} */
function normalizeCategory(category, index = 0) {
  const slug = category?.slug ?? category?.id
  const haystack = `${slug} ${category?.name ?? ''}`.toLowerCase()
  const meta = {
    icon: ICON_RULES.find(([re]) => re.test(haystack))?.[1] ?? 'package',
    color: CARD_COLORS[index % CARD_COLORS.length],
  }
  return {
    ...category,
    backendId: category?.id,
    id: slug,
    slug,
    name: category?.name ?? slug,
    icon: category?.icon ?? meta.icon,
    color: category?.color ?? meta.color,
    // `productCount` (live products, parents include their sub-categories)
    // is what the API sends now; the older shapes are for the mock data.
    productCount: category?.productCount ?? category?._count?.products ?? category?.products?.length ?? 0,
  }
}

function flattenTree(tree) {
  const out = []
  const walk = (nodes) => {
    for (const node of nodes ?? []) {
      const { children, ...rest } = node
      out.push(normalizeCategory(rest, out.length))
      if (children?.length) walk(children)
    }
  }
  walk(tree)
  return out
}

/**
 * Nest a flat (normalised) list back into parents with `children`, in the
 * original order. Storefront menus and filters want the hierarchy; the flat
 * list is what everything else uses.
 * @param {Category[]} flat
 * @returns {Array<Category & { children: Category[] }>}
 */
export function buildCategoryTree(flat) {
  const byBackendId = new Map(flat.map((c) => [c.backendId, { ...c, children: [] }]))
  const roots = []
  for (const c of flat) {
    const node = byBackendId.get(c.backendId)
    const parent = c.parentId ? byBackendId.get(c.parentId) : null
    if (parent) parent.children.push(node)
    else roots.push(node)
  }
  return roots
}

/**
 * Selected category slugs plus every descendant's slug, so filtering by a
 * parent also shows the products filed under its sub-categories.
 * @param {string[]} slugs
 * @param {Category[]} flat
 */
export function withDescendants(slugs, flat) {
  if (slugs.length === 0) return slugs
  const childrenOf = new Map()
  for (const c of flat) {
    if (!c.parentId) continue
    const parent = flat.find((p) => p.backendId === c.parentId)
    if (!parent) continue
    if (!childrenOf.has(parent.id)) childrenOf.set(parent.id, [])
    childrenOf.get(parent.id).push(c.id)
  }
  const out = new Set()
  const walk = (slug) => {
    if (out.has(slug)) return
    out.add(slug)
    for (const child of childrenOf.get(slug) ?? []) walk(child)
  }
  slugs.forEach(walk)
  return [...out]
}

/** @returns {Promise<Category[]>} */
export async function listCategories() {
  if (isRemoteApiEnabled()) {
    const tree = await apiClient.get('/categories')
    return flattenTree(Array.isArray(tree) ? tree : [])
  }
  return /** @type {Category[]} */ (categoriesData)
}

// ---------------------------------------------------------------- admin API

/**
 * The raw category tree, un-normalised.
 *
 * Admin screens need this rather than `listCategories()`: normalisation rewrites
 * `id` to the slug and hides the real UUID under `backendId`, but PATCH and
 * DELETE are keyed by UUID (the backend puts a `ParseUUIDPipe` on the param).
 * The nesting is preserved here too, which the flattened storefront shape drops.
 *
 * Caveat: `GET /categories` filters to `isActive: true`, so a deactivated
 * category disappears from this list entirely and cannot be reactivated from the
 * admin UI. Reactivating one currently needs a database update — there is no
 * endpoint that lists inactive categories.
 *
 * @returns {Promise<object[]>} roots, each with a `children` array
 */
export async function listCategoryTree() {
  const tree = await apiClient.get('/categories')
  return Array.isArray(tree) ? tree : []
}

/** @param {{ name: string, description?: string, imageUrl?: string, parentId?: string }} dto */
export async function createCategory(dto) {
  return apiClient.post('/categories', dto)
}

/** @param {string} id @param {object} dto */
export async function updateCategory(id, dto) {
  return apiClient.patch(`/categories/${id}`, dto)
}

/**
 * Soft delete — sets `isActive: false`. The category stops appearing anywhere,
 * including this admin list. Products keep their `categoryId`.
 *
 * @param {string} id
 */
export async function deactivateCategory(id) {
  return apiClient.delete(`/categories/${id}`)
}
